import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import IncomeStatementImage from 'assets/images/income-statement-image.svg';
import { StyledCard } from 'components/Custom/card';
import { CardDescSpan, CardTitleLargeSpan } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { StyledTooltip } from 'components/Custom/Tooltip';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MenuSize } from 'store/app/enums';
import type { Organisation } from 'store/organisation/models';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { EntityNarrowSidePanelChrome } from './entity-narrow-side-panel-chrome';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;

interface IncomeStatementTabProps {
  organisation?: Organisation;
  menuSize: MenuSize;
}

export const IncomeStatementTab: React.FC<IncomeStatementTabProps> = ({
  organisation: _organisation,
  menuSize: _menuSize,
}) => {
  const { t } = useTranslation();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [showIntroNarrowPanel, setShowIntroNarrowPanel] = React.useState(false);

  const incomeStatementWideLayout =
    windowWidth > ENTITY_DETAILS_BREAKPOINT &&
    windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;

  React.useEffect(() => {
    if (incomeStatementWideLayout) {
      setShowIntroNarrowPanel(false);
    }
  }, [incomeStatementWideLayout]);

  const renderSortIcon = ({ sortOrder }: { sortOrder?: string }) => {
    if (sortOrder === 'ascend') {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    if (sortOrder === 'descend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
  };

  const publishedReportsColumns = [
    {
      title: t('organisation.generated-by', 'Generated by'),
      dataIndex: 'generatedBy',
      key: 'generatedBy',
      width: 120,
    },
    {
      title: t('organisation.generated-date', 'Generated date'),
      dataIndex: 'generatedDate',
      key: 'generatedDate',
      width: 150,
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
    },
    {
      title: t('main.action-required'),
      key: 'action',
      width: 100,
      render: () => null,
    },
  ];

  const renderPublishedReportsTable = () => (
    <StyledCard
      style={{
        width: '100%',
        minWidth: incomeStatementWideLayout ? 600 : 0,
        flex: 1,
        overflow: 'auto',
        border: '1px solid #e3ebeb',
        boxShadow: '0px 4px 10px 0px #0000000D',
      }}
    >
      <div
        style={{
          padding: '5px',
          minWidth: incomeStatementWideLayout ? 500 : 0,
          margin: 'auto',
        }}
      >
        <ScopeTable
          className="reconciliation-reports-table"
          columns={publishedReportsColumns}
          mainLocaleKey="users"
          originalDataSource={[]}
          tableDataSource={[]}
          pagination={false}
          enableColumnsCustomization={false}
          rowHeight={45}
          titleComponent={
            <div>
              <CardTitleLargeSpan style={{ fontSize: 20, lineHeight: '32px' }}>
                {t('organisation.published-income-statements')}
              </CardTitleLargeSpan>
              <CardDescSpan
                style={{
                  display: 'block',
                  marginTop: 8,
                  color: 'var(--color-text-muted, #5a6a6a)',
                  fontSize: 14,
                }}
              >
                {t('organisation.income-statement-coming-soon-note')}
              </CardDescSpan>
            </div>
          }
          scroll={{ x: 760 }}
          fontSize={12}
          transactionType=""
          reportId=""
        />
      </div>
    </StyledCard>
  );

  const renderIntroSidePanel = () => (
    <StyledCard
      style={{
        width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
        height: 'fit-content',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          alignItems: 'center',
        }}
      >
        <div style={{ width: '100%', textAlign: 'left' }}>
          <CardTitleLargeSpan style={{ marginBottom: 10, display: 'block' }}>
            {t('organisation.income-statement-banner-title')}
          </CardTitleLargeSpan>
          <CardDescSpan style={{ marginBottom: 0, display: 'block' }}>
            {t('organisation.income-statement-banner-desc')}
          </CardDescSpan>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <StyledTooltip
            placement="top"
            title={t('organisation.income-statement-coming-soon-note')}
          >
            <span style={{ display: 'inline-block' }}>
              <ScopeButton
                type="dark-orange"
                width="200px"
                height="40px"
                fontSize="15px"
                lineheight="12px"
                disabled
              >
                {t('organisation.income-statement-new-report')}
              </ScopeButton>
            </span>
          </StyledTooltip>
        </div>
        <img
          alt={t('organisation.income-statement-intro-side-panel-tab')}
          src={IncomeStatementImage}
          style={{
            width: 250,
            height: 'auto',
            display: 'block',
          }}
        />
      </div>
    </StyledCard>
  );

  const narrowMainColumnStyle: React.CSSProperties = {
    width:
      windowWidth > ENTITY_DETAILS_BREAKPOINT &&
      windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT
        ? 'calc(100vw - 552px)'
        : 'calc(100vw - 415px)',
    minWidth: 850,
    marginRight: '24px',
  };

  return (
    <div
      className="entity-income-statement-tab"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div
        className={`income-statement-tab-wrapper${
          !incomeStatementWideLayout
            ? ' income-statement-tab-wrapper--narrow'
            : ''
        }`}
      >
        {incomeStatementWideLayout ? (
          <div
            style={{
              paddingTop: 8,
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 20,
              alignContent: 'flex-start',
            }}
          >
            {renderPublishedReportsTable()}
            {renderIntroSidePanel()}
          </div>
        ) : (
          <div className="entity-overview-tab entity-details-narrow-side-shell">
            <div style={narrowMainColumnStyle}>
              <div
                style={{
                  padding: '0 40px',
                  width: '100%',
                  maxWidth: 1250,
                  margin: '0 auto',
                  paddingTop: 8,
                }}
              >
                {renderPublishedReportsTable()}
              </div>
            </div>
            <EntityNarrowSidePanelChrome
              panelOpen={showIntroNarrowPanel}
              onOpenPanel={() => setShowIntroNarrowPanel(true)}
              onClosePanel={() => setShowIntroNarrowPanel(false)}
              tabLabel={t('organisation.income-statement-intro-side-panel-tab')}
              panelWidthPx={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
            >
              {renderIntroSidePanel()}
            </EntityNarrowSidePanelChrome>
          </div>
        )}
      </div>
    </div>
  );
};
